'use client';

import { useState } from 'react';

import { AcademyFaq } from '@/components/sections/academy-faq';
import { FeaturedCourses } from '@/components/sections/featured-courses';
import { FeaturedLessons } from '@/components/sections/featured-lessons';
import HeroOpenAcademy from '@/components/sections/hero-open-academy';
import { COURSE_CATEGORY, getCourses } from '@/data/courses';
import { useTranslation } from '@/lib/i18n/context';
import { BASE_URL } from '@/lib/metadata-helper';

export default function OpenAcademyPage() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  // Generate JSON-LD for all courses dynamically
  const COURSES = getCourses(t.academy);
  const courseSchemas = COURSES.map((course) => ({
    '@type': 'Course',
    name: course.title,
    description: course.description,
    provider: {
      '@type': 'Organization',
      name: 'Open Academy',
      sameAs: 'https://www.open.cx',
    },
    image: `${BASE_URL}${course.image}`,
    educationalLevel: course.level,
    category: COURSE_CATEGORY,
    // Each card links to a course you can start now, on demand — the same
    // instance the detail page describes. Without it the item is an incomplete
    // Course and the whole node is dropped.
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'online',
      courseWorkload: course.duration,
    },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
  }));

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      ...courseSchemas,
      {
        // Sourced from the same dictionary the visible <AcademyFaq> renders, so
        // the FAQPage markup always matches the on-page answers (a Google
        // requirement) and the Arabic copy lives in exactly one place.
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: t.academy['Why is AI taking over customer support?'],
            acceptedAnswer: {
              '@type': 'Answer',
              text: t.academy[
                "It's not about taking over, it's about handling scale. AI can instantly resolve 50-80% of routine questions (refunds, status checks, FAQs) without making customers wait. This frees up humans to handle the complex, emotional, or high-stakes issues that actually require a human touch."
              ],
            },
          },
          {
            '@type': 'Question',
            name: t.academy['Are these courses free?'],
            acceptedAnswer: {
              '@type': 'Answer',
              text: t.academy[
                'Yes. We believe the future of support should be accessible to everyone. The core curriculum at Open Academy is completely free. We want to empower the next generation of support leaders.'
              ],
            },
          },
        ],
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <HeroOpenAcademy onSearch={setSearchQuery} />
      <FeaturedCourses searchQuery={searchQuery} />
      <FeaturedLessons />
      <AcademyFaq />
    </>
  );
}
